import { Injectable, Injector, computed, effect, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PlatformLineTextReviewService } from '@/app/data/_generated-api/platform-api/services/platform-line-text-review.service';
import { PlatformLineTextReviewDto } from '@/app/data/_generated-api/platform-api/models/platform-line-text-review-dto';
import { NOTIFIER } from '@shared/notifier/types';
import {
  LineTextReviewMessageRecord,
  setLineTextReviewChangeListener,
} from '@common/line-text-review-signals/line-text-review-signals';
import { HdChartSettingsStore } from '../chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { HdLineTextReviewAccessService } from './hd-line-text-review-access.service';
import {
  HD_LINE_TEXT_LANGS,
  HD_LINE_TEXTS_PER_LANG,
  HdLineTextField,
  HdLineTextLang,
  HdLineTextReview,
  hdLineTextReviewKey,
} from './hd-line-text-review.metadata';

/** Stand van de review per taal, voor het overzicht en de settings. */
export interface HdLineTextReviewSummary {
  readonly lang: HdLineTextLang;
  readonly total: number;
  readonly approved: number;
  readonly edited: number;
}

/**
 * Uitkomst van een schrijfactie. Bij `conflict` is er niets opgeslagen: iemand anders wijzigde
 * het record na het moment waarop deze client het inlas; `current` is de actuele stand van de
 * server (ook al in de lokale records gezet).
 */
export type HdLineTextReviewSaveOutcome =
  | { readonly status: 'saved' }
  | { readonly status: 'conflict'; readonly current: HdLineTextReview }
  | { readonly status: 'error' };

/** Een wijziging door een andere reviewer, live binnengekomen via SignalR. */
export interface HdLineTextReviewRemoteChange {
  readonly key: string;
  readonly review: HdLineTextReview;
  /** Volgnummer zodat twee opeenvolgende wijzigingen aan dezelfde tekst allebei gezien worden. */
  readonly sequence: number;
}

/**
 * Reviewstatus van de eigen lijnteksten (goedgekeurd / herschreven), gedeeld
 * door alle gebruikers en bewaard in de backend (`api/line-text-reviews`).
 *
 * - Laadt één keer zodra de eigen lijnteksten aan staan (een herschreven tekst
 *   geldt voor iedereen) of de reviewfeature toegekend is.
 * - {@link effectiveText} legt een herschreven tekst over de brontekst uit de
 *   scope `gate-lines-own`; zonder record blijft de brontekst gelden.
 * - Schrijven gebeurt met een optimistische update en botsingscontrole: de
 *   server krijgt het `updatedOn` mee dat deze client kende en weigert te
 *   overschrijven wanneer iemand anders eerst was (`conflict`, met de actuele
 *   stand). Mislukt de call, dan komt het vorige record terug en meldt een
 *   toast de fout.
 * - Wijzigingen van andere reviewers komen live binnen via het
 *   `LineTextReviewChanged`-seintje (PlatformInstanceListener) en worden
 *   meteen in de records gezet; {@link lastRemoteChange} laat een open editor
 *   waarschuwen.
 *
 * De API-client en de notifier worden lui via de injector opgehaald zodat
 * component-specs zonder HttpClient of toast-module de componenten gewoon
 * kunnen renderen (zie hd-gate-line.component.spec.ts).
 */
@Injectable({ providedIn: 'root' })
export class HdLineTextReviewService {
  private readonly injector = inject(Injector);
  private readonly settings = inject(HdChartSettingsStore);
  private readonly access = inject(HdLineTextReviewAccessService);

  private readonly records = signal<ReadonlyMap<string, HdLineTextReview>>(new Map());
  private loadPromise: Promise<void> | null = null;
  private remoteSequence = 0;

  /** Eerste load afgerond (ook na een fout, zodat de UI niet blijft wachten). */
  readonly loaded = signal(false);
  readonly loadError = signal(false);
  /** Sleutels (zie {@link hdLineTextReviewKey}) waarvoor een schrijfactie loopt. */
  readonly saving = signal<ReadonlySet<string>>(new Set());
  /**
   * Doorklik vanuit het overzicht: de eerste eigen lijnbeschrijving van deze
   * poort/lijn die rendert scrollt zichzelf in beeld en wist dit weer.
   */
  readonly pendingFocus = signal<{ readonly gate: number; readonly line: number } | null>(null);
  /** Laatste wijziging van een andere reviewer (live), voor de waarschuwing in een open editor. */
  readonly lastRemoteChange = signal<HdLineTextReviewRemoteChange | null>(null);

  /** Alle records, gesorteerd op poort, lijn, taal en veld. */
  readonly all = computed<readonly HdLineTextReview[]>(() => [...this.records().values()]);

  readonly summaries = computed<readonly HdLineTextReviewSummary[]>(() => {
    const all = this.all();
    return HD_LINE_TEXT_LANGS.map(lang => ({
      lang,
      total: HD_LINE_TEXTS_PER_LANG,
      approved: all.filter(r => r.lang === lang && r.approved).length,
      edited: all.filter(r => r.lang === lang && r.text != null).length,
    }));
  });

  constructor() {
    effect(() => {
      if (this.settings.ownLineTextsEnabled() || this.access.featureGranted()) {
        void this.ensureLoaded();
      }
    });
    // Live seintjes van andere reviewers (eigen echo filtert het kanaal al weg).
    setLineTextReviewChangeListener(record => this.applyRemote(record));
  }

  /** Laadt de records één keer; herhaalde aanroepen delen dezelfde load. */
  ensureLoaded(): Promise<void> {
    this.loadPromise ??= this.load();
    return this.loadPromise;
  }

  /** Haalt alles opnieuw op (bv. na een fout of na een lange onderbreking). */
  reload(): Promise<void> {
    this.loadPromise = this.load();
    return this.loadPromise;
  }

  record(gate: number, line: number, lang: string, field: HdLineTextField): HdLineTextReview | undefined {
    return this.records().get(hdLineTextReviewKey(gate, line, lang, field));
  }

  /** Herschreven tekst wanneer die er is, anders de brontekst uit de scope. */
  effectiveText(gate: number, line: number, lang: string, field: HdLineTextField, sourceText: string): string {
    return this.record(gate, line, lang, field)?.text ?? sourceText;
  }

  isSaving(gate: number, line: number, lang: string, field: HdLineTextField): boolean {
    return this.saving().has(hdLineTextReviewKey(gate, line, lang, field));
  }

  /** Vinkje: goedkeuren of de goedkeuring intrekken; een herschreven tekst blijft staan. */
  setApproved(gate: number, line: number, lang: HdLineTextLang, field: HdLineTextField, approved: boolean): Promise<HdLineTextReviewSaveOutcome> {
    const current = this.record(gate, line, lang, field);
    return this.upsert({ gate, line, lang, field, text: current?.text ?? null, approved }, current?.updatedOn ?? null);
  }

  /**
   * Opslaan na bewerken: de tekst wordt bewaard én goedgekeurd. Een tekst die
   * gelijk is aan de brontekst wordt als "brontekst behouden" opgeslagen.
   *
   * `expectedUpdatedOn` is het tijdstip van het record op het moment dat de
   * editor openging (null = de editor kende geen record). De server vergelijkt
   * het met de actuele stand; wijkt die af, dan komt `conflict` terug en is er
   * niets overschreven. Weggelaten → het lokaal bekende record geldt.
   */
  saveText(
    gate: number,
    line: number,
    lang: HdLineTextLang,
    field: HdLineTextField,
    text: string,
    sourceText: string,
    expectedUpdatedOn?: string | null,
  ): Promise<HdLineTextReviewSaveOutcome> {
    const trimmed = text.trim();
    const keepsSource = trimmed.length === 0 || trimmed === sourceText.trim();
    const expected = expectedUpdatedOn === undefined
      ? this.record(gate, line, lang, field)?.updatedOn ?? null
      : expectedUpdatedOn;
    return this.upsert({ gate, line, lang, field, text: keepsSource ? null : trimmed, approved: true }, expected);
  }

  requestFocus(gate: number, line: number): void {
    this.pendingFocus.set({ gate, line });
  }

  /** Neemt de gevraagde focus voor deze poort/lijn op; `true` wanneer de aanroeper moet scrollen. */
  consumeFocus(gate: number, line: number): boolean {
    const focus = this.pendingFocus();
    if (focus == null || focus.gate !== gate || focus.line !== line) {
      return false;
    }
    this.pendingFocus.set(null);
    return true;
  }

  private async upsert(
    input: Omit<HdLineTextReview, 'updatedByDisplayName' | 'updatedOn'>,
    expectedUpdatedOn: string | null,
  ): Promise<HdLineTextReviewSaveOutcome> {
    const key = hdLineTextReviewKey(input.gate, input.line, input.lang, input.field);
    const previous = this.records().get(key);
    this.markSaving(key, true);
    // Optimistisch: de UI toont de nieuwe stand meteen.
    this.patch(key, { ...input, updatedByDisplayName: previous?.updatedByDisplayName ?? null, updatedOn: new Date().toISOString() });
    try {
      const result = await firstValueFrom(this.api().postLineTextReviewsActionUpsert({
        body: {
          gate: input.gate, line: input.line, lang: input.lang, field: input.field,
          text: input.text ?? undefined, approved: input.approved,
          expectedUpdatedOn: expectedUpdatedOn ?? undefined,
        },
      }));
      if (!result?.isValid || !result.value?.review) {
        throw new Error(result?.simpleMessage || 'De reviewstatus kon niet bewaard worden.');
      }
      const review = toReview(result.value.review);
      this.patch(key, review);
      return result.value.conflict ? { status: 'conflict', current: review } : { status: 'saved' };
    } catch (error) {
      this.restore(key, previous);
      this.notify('error', error instanceof Error ? error.message : 'De reviewstatus kon niet bewaard worden.');
      return { status: 'error' };
    } finally {
      this.markSaving(key, false);
    }
  }

  private applyRemote(record: LineTextReviewMessageRecord): void {
    const review = toReview(record as PlatformLineTextReviewDto);
    const key = hdLineTextReviewKey(review.gate, review.line, review.lang, review.field);
    this.patch(key, review);
    this.lastRemoteChange.set({ key, review, sequence: ++this.remoteSequence });
  }

  private async load(): Promise<void> {
    this.loadError.set(false);
    try {
      const result = await firstValueFrom(this.api().getLineTextReviews());
      if (!result?.isValid) {
        throw new Error(result?.simpleMessage || 'De reviewstatus kon niet geladen worden.');
      }
      const next = new Map<string, HdLineTextReview>();
      for (const dto of result.value ?? []) {
        const review = toReview(dto);
        next.set(hdLineTextReviewKey(review.gate, review.line, review.lang, review.field), review);
      }
      this.records.set(next);
    } catch {
      // Geen toast: de teksten zelf blijven gewoon zichtbaar (brontekst); het
      // overzicht toont de fout met een knop om opnieuw te laden.
      this.loadError.set(true);
    } finally {
      this.loaded.set(true);
    }
  }

  private patch(key: string, review: HdLineTextReview): void {
    this.records.update(map => {
      const next = new Map(map);
      next.set(key, review);
      return sortByKey(next);
    });
  }

  private restore(key: string, previous: HdLineTextReview | undefined): void {
    if (previous) {
      this.patch(key, previous);
      return;
    }
    this.records.update(map => {
      const next = new Map(map);
      next.delete(key);
      return next;
    });
  }

  private markSaving(key: string, saving: boolean): void {
    this.saving.update(set => {
      const next = new Set(set);
      if (saving) next.add(key); else next.delete(key);
      return next;
    });
  }

  private api(): PlatformLineTextReviewService {
    return this.injector.get(PlatformLineTextReviewService);
  }

  private notify(kind: 'error' | 'success', message: string): void {
    const notifier = this.injector.get(NOTIFIER, null, { optional: true });
    notifier?.[kind](message);
  }
}

function toReview(dto: PlatformLineTextReviewDto): HdLineTextReview {
  return {
    gate: dto.gate ?? 0,
    line: dto.line ?? 0,
    lang: (dto.lang ?? 'nl') as HdLineTextLang,
    field: (dto.field ?? 'description') as HdLineTextField,
    text: dto.text ?? null,
    approved: dto.approved ?? false,
    updatedByDisplayName: dto.updatedByDisplayName ?? null,
    updatedOn: dto.updatedOn ?? '',
  };
}

function sortByKey(map: Map<string, HdLineTextReview>): Map<string, HdLineTextReview> {
  const order = (r: HdLineTextReview): string =>
    `${String(r.gate).padStart(2, '0')}.${r.line}.${r.lang}.${['description', 'exaltation', 'detriment'].indexOf(r.field)}`;
  return new Map([...map.entries()].sort(([, a], [, b]) => order(a).localeCompare(order(b))));
}
